import type { KlaroConfig } from 'klaro/dist/klaro-no-css';
import posthog from 'posthog-js';
import Clarity from '@microsoft/clarity';
import { isProduction } from '@/utils/env';

const POSTHOG_KEY = 'phc_tKXQ4Ylowr5ZnAV9xNcMdupVDApxdet2EWSoNACYMIN';
const POSTHOG_HOST = 'https://us.i.posthog.com';
const CLARITY_PROJECT_ID = 'p1k5rnbc9o';

let posthogInitialized = false;
let clarityInitialized = false;

/**
 * Initialize PostHog with cookieless mode on reject.
 * With `cookieless_mode: 'on_reject'`, no events are captured until the user
 * makes a consent decision. After the decision:
 * - Accept: Full cookie-based tracking via opt_in_capturing()
 * - Reject: Cookieless tracking with server-side hashing via opt_out_capturing()
 * @see https://posthog.com/docs/libraries/js#cookie-consent
 */
export const initializePostHog = (): void => {
  if (posthogInitialized || !isProduction()) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    cookieless_mode: 'on_reject',
    defaults: '2025-11-30',
  });
  posthogInitialized = true;
};

/**
 * Initialize Microsoft Clarity.
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-consent-api-v2
 */
export const initializeClarity = (): void => {
  if (clarityInitialized || !isProduction()) return;

  Clarity.init(CLARITY_PROJECT_ID);
  clarityInitialized = true;
};

/**
 * Klaro consent manager configuration.
 *
 * CONSENT STRATEGY: We use `mustConsent: false` to show a non-blocking corner
 * banner instead of a modal. Combined with `cookieless_mode: 'on_reject'`, this
 * means users who ignore the banner are not tracked at all.
 *
 * This is a conscious trade-off:
 * - PRO: Better UX (non-intrusive banner)
 * - PRO: Privacy-respecting (no tracking without explicit decision)
 * - CON: Users who ignore the banner = no analytics data
 *
 * We accept this data loss because:
 * 1. Most legitimate users will eventually interact with the banner
 * 2. Non-interacting users are likely bots, immediate bouncers, or privacy-conscious
 * 3. This aligns with GDPR's spirit of informed consent
 *
 * @see https://posthog.com/tutorials/cookieless-tracking
 */
export const klaroConfig: KlaroConfig = {
  elementID: 'klaro',
  storageMethod: 'cookie',
  storageName: 'washi_consent',
  cookieExpiresAfterDays: 365,
  default: false,
  // Show corner banner, not blocking modal. Users who ignore = not tracked (intentional).
  mustConsent: false,
  acceptAll: true,
  hideDeclineAll: false,
  hideLearnMore: false,
  htmlTexts: true,

  translations: {
    zz: {
      privacyPolicyUrl: '/privacy',
    },
    en: {
      privacyPolicyUrl: '/privacy',
      consentNotice: {
        description:
          'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.',
        learnMore: 'Customize',
      },
      consentModal: {
        title: 'Cookie Preferences',
        description:
          'Select which cookies you want to allow. You can change these settings at any time.',
      },
      acceptAll: 'Accept All',
      acceptSelected: 'Save Preferences',
      decline: 'Reject All',
      ok: 'Accept All',
      purposes: {
        analytics: {
          title: 'Analytics',
          description:
            'These services help us understand how you use our site so we can improve it.',
        },
        functionality: {
          title: 'Functionality',
          description: 'These services enable enhanced functionality and personalization.',
        },
      },
      poweredBy: '',
    },
  },

  services: [
    {
      name: 'posthog',
      default: false,
      required: false,
      optOut: false,
      onlyOnce: true,
      translations: {
        zz: {
          title: 'PostHog Analytics',
        },
        en: {
          title: 'PostHog Analytics',
          description:
            'PostHog helps us understand how you use our application to improve your experience.',
        },
      },
      purposes: ['analytics'],
      cookies: [
        [/^ph_.*$/, '/', window.location.hostname],
        ['__ph_opt_in_out', '/', window.location.hostname],
      ],
      callback: (consent: boolean): void => {
        // Ensure PostHog is initialized (normally done at app startup in app.tsx)
        if (!posthogInitialized) {
          initializePostHog();
        }
        // Only signal consent if PostHog was actually initialized (production only)
        if (!posthogInitialized) return;

        // Signal consent decision to PostHog
        // With cookieless_mode: 'on_reject', PostHog runs in cookieless mode by default.
        // opt_in_capturing() enables full tracking with cookies.
        // opt_out_capturing() keeps it in cookieless mode.
        if (consent) {
          posthog.opt_in_capturing();
        } else {
          posthog.opt_out_capturing();
        }
      },
    },
    {
      name: 'clarity',
      default: false,
      required: false,
      optOut: false,
      onlyOnce: true,
      translations: {
        zz: {
          title: 'Microsoft Clarity',
        },
        en: {
          title: 'Microsoft Clarity',
          description:
            'Clarity helps us understand how you interact with our site through session recordings and heatmaps.',
        },
      },
      purposes: ['analytics'],
      cookies: [
        ['_clck', '/', window.location.hostname],
        ['_clsk', '/', window.location.hostname],
        [/^_cl.*$/, '/', window.location.hostname],
        ['CLID', '/', '.clarity.ms'],
        ['ANONCHK', '/', '.c.clarity.ms'],
        ['MR', '/', '.c.clarity.ms'],
        ['MUID', '/', '.clarity.ms'],
        ['SM', '/', '.c.clarity.ms'],
      ],
      callback: (consent: boolean): void => {
        // Ensure Clarity is initialized (normally done at app startup in app.tsx)
        if (!clarityInitialized) {
          initializeClarity();
        }
        // Only signal consent if Clarity was actually initialized (production only)
        if (!clarityInitialized) return;

        // Signal consent decision to Clarity
        // Clarity waits for consentV2() before enabling full tracking.
        Clarity.consentV2({
          ad_Storage: 'denied', // We don't use ads
          analytics_Storage: consent ? 'granted' : 'denied',
        });
      },
    },
  ],
};

export default klaroConfig;
